'use client';

import { useState } from 'react';
import { ChevronDown, Settings, Replace, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { rendersService as renderService } from '@/shared/api/services/renders.service';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import { Button } from '@/shared/ui/shadcn/button';
import type { RenderControl } from '@/types/render';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import type { RenderCreateRequest } from '@/types/render';
import { ClassTypes } from '@/shared/constants/class-types';
import { generateSeed } from '@/shared/constants/generation';

interface DerivativeAngleActionsProps {
  projectId: string;
  conceptId: string;
  angleId: string;
  conceptAngleId: string;
  angleName: string;
  onReplacePhoto?: () => void;
  onDeleteAngle?: () => void;
  onDeleteRender?: (renderId: string) => void;
  onRenderCreated?: () => void;
  onSettingsClick?: () => void;
  currentRenderId?: string;
  isRenderInProgress?: boolean;
  hasActiveRenders?: boolean;
  className?: string;
  hasMainRender?: boolean;
  hasExistingRender?: boolean;
  lastRenderSettings?: {
    prompts?: { prompt_angle?: string };
    seed?: number;
    controls?: RenderControl[] | null;
  };
}

export function DerivativeAngleActions({
  conceptId,
  conceptAngleId,
  onReplacePhoto,
  onDeleteAngle,
  onDeleteRender,
  onRenderCreated,
  onSettingsClick,
  currentRenderId,
  isRenderInProgress = false,
  hasActiveRenders = false,
  className = '',
  hasMainRender = false,
  hasExistingRender = false,
  lastRenderSettings,
}: DerivativeAngleActionsProps) {
  const t = useTranslations('render.options');
  const tButton = useTranslations('render.button');
  const tCommon = useTranslations('common');
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteAngleDialog, setShowDeleteAngleDialog] = useState(false);
  const [showDeleteRenderDialog, setShowDeleteRenderDialog] = useState(false);

  const handleRender = async () => {
    if (!hasMainRender) return;

    try {
      setIsCreating(true);

      // Always generate a new seed for re-render to ensure a new version is created
      // This prevents the backend from returning an existing render with the same parameters
      const newSeed = generateSeed();

      // Create render with last used settings or defaults
      const renderData: RenderCreateRequest = {
        data: {
          type: ClassTypes.RENDER,
          attributes: {
            version: 1,
            seed: newSeed, // Always use new seed for re-render
            prompts: {
              prompt_concept: '',
              prompt_angle: lastRenderSettings?.prompts?.prompt_angle ?? '',
            },
            controls: lastRenderSettings?.controls ?? null,
            // These will be inherited from main render on backend
            style_ids: null,
            material_ids: [],
            colors: null,
            output_style_id: null,
          },
        },
      };

      await renderService.createRender(conceptId, renderData, conceptAngleId);

      // Notify parent - render progress will be shown inline
      if (onRenderCreated) {
        onRenderCreated();
      }
    } catch (err: unknown) {
      console.error('Error creating derivative render:', err);

      // Surface the server-localized error verbatim. The 429 limit-reached
      // path is upstream of W1.5 paywall routing — until that lands, we trust
      // the API's localized text (Accept-Language is set on the request) rather
      // than substitute hardcoded English copy here.
      toast.error(
        err instanceof ApiClientError
          ? err.getFirstError()
          : tCommon('errors.generic')
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenderSettings = () => {
    // Open settings drawer instead of navigating to render page
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const handleReplacePhoto = () => {
    if (onReplacePhoto) {
      onReplacePhoto();
    }
  };

  const handleDeleteAngle = () => {
    setShowDeleteAngleDialog(false);
    if (onDeleteAngle) {
      onDeleteAngle();
    }
  };

  const handleDeleteRender = () => {
    if (!currentRenderId) return;
    setShowDeleteRenderDialog(false);
    if (onDeleteRender) {
      onDeleteRender(currentRenderId);
    }
  };

  return (
    <>
      <div className={`inline-flex items-stretch gap-0.5 ${className}`}>
        <Button
          onClick={handleRender}
          disabled={!hasMainRender || isCreating}
          className="rounded-r-none font-bold"
          size="default"
        >
          {isCreating
            ? tButton('creating')
            : hasExistingRender
              ? t('reRender')
              : tButton('render')}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="default"
              className="rounded-l-none px-3"
              disabled={!hasMainRender}
              aria-label={t('moreRenderOptions')}
            >
              <ChevronDown className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onClick={handleRenderSettings}
              disabled={!hasMainRender}
            >
              <Settings className="mr-2 h-4 w-4" />
              {t('renderSettings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleReplacePhoto}>
              <Replace className="mr-2 h-4 w-4" />
              {t('replaceSourcePhoto')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!hasActiveRenders && (
              <DropdownMenuItem
                onClick={() => setShowDeleteAngleDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('deleteThisAngle')}
              </DropdownMenuItem>
            )}
            {currentRenderId && !isRenderInProgress && (
              <DropdownMenuItem
                onClick={() => setShowDeleteRenderDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('deleteSelectedRender')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog
        open={showDeleteAngleDialog}
        onOpenChange={setShowDeleteAngleDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteAngleTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteAngleDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAngle}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showDeleteRenderDialog}
        onOpenChange={setShowDeleteRenderDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteRenderTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteRenderDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRender}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
